"use client"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { AlertTriangle, Trash2 } from "lucide-react"

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: "danger" | "warning" | "default"
  icon?: "trash" | "warning" | "none"
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  icon = "warning"
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm()
    onOpenChange(false)
  }

  const getIcon = () => {
    switch (icon) {
      case "trash":
        return <Trash2 className="h-6 w-6 text-red-600" />
      case "warning":
        return <AlertTriangle className="h-6 w-6 text-amber-600" />
      default:
        return null
    }
  }

  const getConfirmButtonClass = () => {
    switch (variant) {
      case "danger":
        return "bg-[#7C1414] hover:bg-[#7C1414]/90 text-white"
      case "warning":
        return "bg-amber-500 hover:bg-amber-600 text-white"
      default:
        return "bg-[#518BC4] hover:bg-[#518BC4]/90 text-white"
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            {icon !== "none" && (
              <div className={`
                flex-shrink-0 rounded-full p-3
                ${variant === "danger" ? "bg-red-100" : ""}
                ${variant === "warning" ? "bg-amber-100" : ""}
                ${variant === "default" ? "bg-blue-100" : ""}
              `}>
                {getIcon()}
              </div>
            )}
            <div className="flex-1">
              <AlertDialogTitle className="text-xl font-bold text-slate-900">
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-2 text-slate-600 leading-relaxed">
                {description}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6">
          <AlertDialogCancel className="border-slate-300 hover:bg-slate-100">
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={getConfirmButtonClass()}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
